import { useState } from "react";
import type { ReactNode } from "react";
import { Link, Navigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { LifeBuoy, Mail, ScrollText, ShieldAlert, ShieldCheck, SlidersHorizontal, UserPlus, Users } from "lucide-react";
import { paths } from "../../app/paths.js";
import { useDocumentTitle } from "../../app/useDocumentTitle.js";
import { ADMIN_AUDIT_KEY, ADMIN_USERS_KEY, useAdminUsers, useMe, useSystemAudit } from "../../data/account.js";
import type { SystemAuditEvent } from "../../api/admin.js";
import { formatDateTime, formatRelativeTime } from "../../lib/format.js";
import { Alert, Button, EmptyState, Page, PageHeader, Skeleton, Tabs, buttonClass } from "../../ui/primitives.js";
import { CreateUserDialog } from "./CreateUserDialog.js";
import { MailPanel } from "./MailPanel.js";
import { PoliciesPanel } from "./PoliciesPanel.js";
import { RecoveryPanel } from "./RecoveryPanel.js";
import { RecoveryRequestsPanel } from "./RecoveryRequestsPanel.js";
import { TRUST_OVERRIDES_KEY, TrustOverridesPanel } from "./TrustOverridesPanel.js";
import { UserRow } from "./UserRow.js";

type Tab = "users" | "policies" | "recovery" | "audit" | "mail";

const ACTION_LABELS: Readonly<Record<string, string>> = {
  setup_completed: "Instanz eingerichtet",
  initial_password_set: "Eigenes Master-Passwort festgelegt",
  password_changed: "Master-Passwort geändert",
  user_created: "Konto angelegt",
  user_temp_password_reset: "Neues Einmalpasswort ausgestellt",
  user_role_changed: "Rolle geändert",
  user_disabled: "Konto deaktiviert",
  user_enabled: "Konto aktiviert",
  user_sessions_revoked: "Alle Sitzungen beendet",
  user_session_revoked: "Gerät abgemeldet (Verwaltung)",
  session_revoked: "Gerät abgemeldet",
  other_sessions_revoked: "Alle anderen Geräte abgemeldet",
  user_deleted: "Konto gelöscht",
  recovery_key_created: "Notfall-Kit erstellt",
  recovery_key_rotated: "Neues Notfall-Kit erstellt",
  password_recovered: "Master-Passwort mit Notfall-Kit zurückgesetzt",
  policies_changed: "Richtlinien geändert",
  vault_exported: "Tresor exportiert",
  admin_recovery_enabled: "Admin-Wiederherstellung eingerichtet",
  admin_recovery_disabled: "Admin-Wiederherstellung entfernt",
  admin_recovery_granted: "Wiederherstellungsschlüssel freigegeben",
  admin_recovery_enrolled: "Für Admin-Wiederherstellung registriert",
  admin_recovery_unenrolled: "Admin-Wiederherstellung verlassen",
  user_recovered_by_admin: "Zugang per Admin-Wiederherstellung zurückgesetzt",
  recovered_password_set: "Neues Master-Passwort nach Wiederherstellung",
  recovery_requested: "Wiederherstellung beantragt",
  recovery_approved: "Wiederherstellung bestätigt",
  recovery_cancelled: "Wiederherstellung abgebrochen",
  recovery_expired: "Wiederherstellungsanfrage abgelaufen",
  user_2fa_reset_by_admin: "Zwei-Faktor-Anmeldung per Admin-Wiederherstellung zurückgesetzt",
  device_trusted: "Neues Gerät freigegeben",
  device_untrusted: "Gerätefreigabe entzogen",
  trust_override_requested: "Freigabe des nächsten Geräts beantragt",
  trust_override_approved: "Freigabe des nächsten Geräts bestätigt",
  trust_override_cancelled: "Freigabe des nächsten Geräts abgebrochen",
  trust_override_expired: "Freigabe des nächsten Geräts abgelaufen",
  trust_override_used: "Neues Gerät per Admin-Freigabe vertraut",
  device_key_registered: "Gerät gemerkt",
  device_key_used: "Anmeldung mit gemerktem Gerät ohne zweiten Faktor",
  device_key_revoked: "Gemerktes Gerät vergessen",
  device_key_expired: "Gemerktes Gerät abgelaufen",
  session_unlocked: "Gesperrte Sitzung entsperrt",
  session_unlock_failed: "Entsperren fehlgeschlagen (falsches Master-Passwort)",
  extension_linked: "Erweiterung per Passkey verbunden",
  extension_link_denied: "Verbinden der Erweiterung abgelehnt (falscher Code)"
};

export function AdminPage(): ReactNode {
  useDocumentTitle("Verwaltung");
  const me = useMe();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>("users");
  const [creating, setCreating] = useState(false);
  const allowed = me.data?.role === "admin" && me.data.twoFactorEnabled;
  const users = useAdminUsers(allowed && (tab === "users" || tab === "policies"));
  const audit = useSystemAudit(allowed && tab === "audit");

  if (me.isPending) {
    return (
      <Page>
        <Skeleton className="h-24" />
      </Page>
    );
  }
  if (me.data === undefined || me.data.role !== "admin") {
    return <Navigate to={paths.dashboard} replace />;
  }

  function refresh(): void {
    void queryClient.invalidateQueries({ queryKey: ADMIN_USERS_KEY });
    void queryClient.invalidateQueries({ queryKey: ADMIN_AUDIT_KEY });
    void queryClient.invalidateQueries({ queryKey: TRUST_OVERRIDES_KEY });
  }

  const pending = (users.data ?? []).filter((u) => u.status === "pending" || u.status === "expired").length;

  return (
    <Page>
      <PageHeader
        eyebrow="Administration"
        title="Verwaltung"
        description="Konten anlegen, sperren und entfernen. Inhalte der Tresore sind für Administratoren nicht einsehbar; die Admin-Wiederherstellung setzt nur den Zugang registrierter Konten zurück."
        actions={allowed ? (
          <Button icon={UserPlus} onClick={(): void => setCreating(true)}>
            Konto anlegen
          </Button>
        ) : undefined}
      />

      {!me.data.twoFactorEnabled ? (
        <div className="mt-6">
          <EmptyState icon={ShieldAlert} title="Zwei-Faktor-Anmeldung erforderlich" action={
            <Link to={`${paths.settings}?setup=totp`} className={buttonClass("primary", "md")}>
              Jetzt einrichten
            </Link>
          }>
            Die Verwaltung ist erst mit Authenticator-App oder Passkey freigeschaltet. So reicht ein bekanntes Master-Passwort allein nicht, um Konten zu übernehmen.
          </EmptyState>
        </div>
      ) : (
        <>
          <div className="mt-4">
            <Tabs
              label="Bereich"
              value={tab}
              onChange={setTab}
              options={[
                { value: "users", label: "Konten", icon: Users },
                { value: "policies", label: "Richtlinien", icon: SlidersHorizontal },
                { value: "recovery", label: "Wiederherstellung", icon: LifeBuoy },
                { value: "audit", label: "Protokoll", icon: ScrollText },
                { value: "mail", label: "E-Mail", icon: Mail }
              ]}
            />
          </div>

          {tab === "users" ? (
            <div className="mt-6 flex flex-col gap-4">
              {pending > 0 ? (
                <Alert tone="info">
                  {pending === 1 ? "1 Konto wartet" : `${pending} Konten warten`} auf die Anmeldung mit einem Einmalpasswort. Einmalpasswörter gelten 72 Stunden.
                </Alert>
              ) : null}
              <TrustOverridesPanel selfId={me.data.id} />
              {users.isPending ? <Skeleton className="h-40" /> : null}
              {users.error !== null ? <Alert tone="danger">{users.error.message}</Alert> : null}
              {users.data !== undefined ? (
                <ul className="divide-y divide-line overflow-hidden rounded-xl border border-line bg-ink-2">
                  {users.data.map((user) => (
                    <UserRow key={user.id} user={user} self={user.id === me.data.id} onChanged={refresh} />
                  ))}
                </ul>
              ) : null}
            </div>
          ) : tab === "policies" ? (
            <div className="mt-6">
              <PoliciesPanel users={users.data} />
            </div>
          ) : tab === "recovery" ? (
            <div className="mt-6 flex flex-col gap-4">
              <RecoveryRequestsPanel selfId={me.data.id} />
              <RecoveryPanel selfId={me.data.id} />
            </div>
          ) : tab === "mail" ? (
            <div className="mt-6">
              <MailPanel email={me.data.email} />
            </div>
          ) : (
            <div className="mt-6">
              {audit.isPending ? <Skeleton className="h-40" /> : null}
              {audit.error !== null ? <Alert tone="danger">{audit.error.message}</Alert> : null}
              {audit.data !== undefined && audit.data.length === 0 ? (
                <EmptyState icon={ScrollText} title="Noch keine Einträge" />
              ) : null}
              {audit.data !== undefined && audit.data.length > 0 ? (
                <ol className="divide-y divide-line overflow-hidden rounded-xl border border-line bg-ink-2">
                  {audit.data.map((event: SystemAuditEvent) => (
                    <li key={event.id} className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-center sm:gap-4">
                      <span className="flex min-w-0 flex-1 items-center gap-2 text-sm text-foam">
                        <ShieldCheck size={14} aria-hidden="true" className="shrink-0 text-flow" />
                        <span className="truncate">
                          <span className="font-medium">{ACTION_LABELS[event.action] ?? event.action}</span>
                          {event.targetEmail !== null && event.targetId !== event.actorId ? <span className="text-mist"> · {event.targetEmail}</span> : null}
                          {event.targetEmail === null && event.targetId !== null && event.targetId !== event.actorId ? <span className="text-mist-2"> · gelöschtes Konto</span> : null}
                        </span>
                      </span>
                      <span className="truncate text-[0.8125rem] text-mist-2">{event.actorEmail ?? (event.actorId === null ? "System" : "gelöschtes Konto")}</span>
                      <span className="shrink-0 text-[0.8125rem] text-mist-2" title={formatDateTime(event.createdAt)}>
                        {formatRelativeTime(event.createdAt, Date.now())}
                      </span>
                    </li>
                  ))}
                </ol>
              ) : null}
            </div>
          )}
        </>
      )}

      {creating ? <CreateUserDialog onClose={(): void => setCreating(false)} onCreated={refresh} /> : null}
    </Page>
  );
}
